import React from 'react'
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons'
import { Card, Col, Row, Statistic, Table, Tag, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'

import {
    getMosquitoCategoryDisplayName,
    normalizeConfiguredDisplayCategory,
    sortMosquitoDisplayCategories,
    type MosquitoDisplayCategory,
} from '../../../utils/mosquitoDisplay'
import './ComparisonAnalysis.css'

interface ComparisonAnalysisProps {
    currentData: {
        total: number
        byType: Array<{ name: string; count: number }>
    }
    previousData: {
        total: number
        byType: Array<{ name: string; count: number }>
    }
}

export const ComparisonAnalysis: React.FC<ComparisonAnalysisProps> = ({
    currentData,
    previousData,
}) => {
    const { t } = useTranslation('statistics')
    const aggregateByCategory = (
        items: Array<{ name: string; count: number }>
    ) =>
        items.reduce<Partial<Record<MosquitoDisplayCategory, number>>>(
            (accumulator, item) => {
                const category = normalizeConfiguredDisplayCategory(item.name)
                if (category) {
                    accumulator[category] = (accumulator[category] || 0) + item.count
                }
                return accumulator
            },
            {}
        )
    const currentByType = aggregateByCategory(currentData.byType)
    const previousByType = aggregateByCategory(previousData.byType)
    const currentTotal = Object.values(currentByType).reduce<number>(
        (total, count) => total + (count ?? 0),
        0
    )
    const previousTotal = Object.values(previousByType).reduce<number>(
        (total, count) => total + (count ?? 0),
        0
    )
    const totalChange =
        previousTotal === 0
            ? currentTotal === 0
                ? 0
                : 100
            : ((currentTotal - previousTotal) / previousTotal) * 100
    const comparisonTypeNames = sortMosquitoDisplayCategories([
        ...Object.keys(currentByType),
        ...Object.keys(previousByType),
    ] as MosquitoDisplayCategory[])

    const typeComparison = comparisonTypeNames.map((typeName) => {
        const currentCount = currentByType[typeName] || 0
        const prevCount = previousByType[typeName] || 0
        const change =
            prevCount === 0
                ? currentCount === 0
                    ? 0
                    : 100
                : ((currentCount - prevCount) / prevCount) * 100

        return {
            name: getMosquitoCategoryDisplayName(typeName, t, 'pestTypes'),
            rawName: typeName,
            current: currentCount,
            previous: prevCount,
            change,
        }
    })
    const changeColumnTitle = (
        <span className="comparison-change-title">
            <span>{t('charts.comparison.change')}</span>
            <Tooltip title={t('charts.comparison.changeTooltip')}>
                <InfoCircleOutlined
                    aria-label={t('charts.comparison.changeTooltip')}
                    className="comparison-change-help"
                />
            </Tooltip>
        </span>
    )

    const columns = [
        {
            title: t('charts.comparison.pestType'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('charts.comparison.currentPeriod'),
            dataIndex: 'current',
            key: 'current',
        },
        {
            title: t('charts.comparison.previousPeriod'),
            dataIndex: 'previous',
            key: 'previous',
        },
        {
            title: changeColumnTitle,
            dataIndex: 'change',
            key: 'change',
            showSorterTooltip: { target: 'sorter-icon' as const },
            render: (val: number) => (
                <span>
                    {val > 0 ? (
                        <Tag color="red">
                            <ArrowUpOutlined /> {val.toFixed(1)}%
                        </Tag>
                    ) : val < 0 ? (
                        <Tag color="green">
                            <ArrowDownOutlined /> {Math.abs(val).toFixed(1)}%
                        </Tag>
                    ) : (
                        <Tag color="blue">0%</Tag>
                    )}
                </span>
            ),
            sorter: (a: { change: number }, b: { change: number }) =>
                a.change - b.change,
        },
    ]
    return (
        <Card title={t('charts.comparison.title')}>
            <Row gutter={[24, 24]}>
                <Col span={24} md={8}>
                    <Statistic
                        title={t('charts.comparison.currentTotal')}
                        value={currentTotal}
                        precision={0}
                    />
                </Col>
                <Col span={24} md={8}>
                    <Statistic
                        title={t('charts.comparison.previousTotal')}
                        value={previousTotal}
                        precision={0}
                    />
                </Col>
                <Col span={24} md={8}>
                    <Statistic
                        title={t('charts.comparison.change')}
                        value={totalChange}
                        precision={2}
                        valueStyle={{
                            color: totalChange >= 0 ? '#cf1322' : '#3f8600',
                        }}
                        prefix={
                            totalChange >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />
                        }
                        suffix="%"
                    />
                </Col>
                <Col span={24}>
                    <Table
                        dataSource={typeComparison}
                        columns={columns}
                        pagination={false}
                        rowKey="rawName"
                    />
                </Col>
            </Row>
        </Card>
    )
}
